import React, { useMemo } from "react";
import { Stack, Text } from "@fluentui/react";

interface RawIndicatorRow {
  sectionName?: string;
  metricName?: string;
  factorLabel?: string;
  value: string | number;
}

interface Props { rows: RawIndicatorRow[] }

const PURVIEW_PURPLE = "#742774";
const PURVIEW_BG = "#FAF5FF";
const PURVIEW_BORDER = "#CE93D8";

const toNum = (v: string | number | undefined): number | null => {
  if (v === undefined || v === null || String(v).trim() === "") return null;
  const n = Number(String(v).replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : null;
};

const fmtInt = (n: number | null): string =>
  n === null ? "—" : Math.round(n).toLocaleString();

interface ParsedData {
  productAbbr: string;
  mpu: number | null;
  pau: number | null;
  deploymentRate: number | null;
  scoringBins: string;
}

function parse(rows: RawIndicatorRow[]): ParsedData {
  let mpu: number | null = null;
  let pau: number | null = null;
  let deploymentRate: number | null = null;
  let scoringBins = "";
  const productAbbr = rows[0]?.sectionName ?? "—";

  for (const row of rows) {
    const lbl = (row.factorLabel ?? "").trim();
    const val = String(row.value ?? "").trim();
    if (/Monthly Protected Users \(MPU\)/i.test(lbl)) mpu = toNum(row.value);
    else if (/Paid Available Units \(PAU\)/i.test(lbl)) pau = toNum(row.value);
    else if (/Deployment Rate/i.test(lbl)) deploymentRate = toNum(row.value);
    else if (/Scoring Bins/i.test(lbl)) scoringBins = val;
  }
  return { productAbbr, mpu, pau, deploymentRate, scoringBins };
}

interface FactorBoxProps { label: string; value: string; valueColor?: string }
const FactorBox: React.FC<FactorBoxProps> = ({ label, value, valueColor = "#323130" }) => (
  <Stack
    horizontalAlign="center"
    tokens={{ childrenGap: 2 }}
    styles={{
      root: {
        backgroundColor: "#FFFFFF",
        border: "1px solid #E8D5F0",
        borderRadius: 8,
        padding: "8px 10px",
        flex: "1 1 0",
        textAlign: "center",
        minWidth: 80,
      },
    }}
  >
    <Text variant="xSmall" styles={{ root: { color: "#605E5C" } }}>{label}</Text>
    <Text variant="mediumPlus" styles={{ root: { fontWeight: 700, color: valueColor } }}>{value}</Text>
  </Stack>
);

const PurviewDeploymentRawIndicatorCards: React.FC<Props> = ({ rows }) => {
  const data = useMemo(() => parse(rows), [rows]);

  const isCapped = data.deploymentRate !== null && data.deploymentRate > 100;
  const isNull = data.deploymentRate === null && data.pau !== null;
  const displayRate = data.deploymentRate === null
    ? "NULL"
    : isCapped
      ? `${data.deploymentRate?.toFixed(1)}% → 100`
      : `${data.deploymentRate?.toFixed(2)}%`;
  const rateColor = data.deploymentRate === null
    ? "#A19F9D"
    : isCapped
      ? "#C15A00"
      : data.deploymentRate >= 80
        ? "#107C10"
        : data.deploymentRate >= 50
          ? "#FFB900"
          : "#D13438";

  return (
    <Stack
      tokens={{ childrenGap: 14 }}
      styles={{
        root: {
          backgroundColor: PURVIEW_BG,
          border: `1.5px solid ${PURVIEW_BORDER}`,
          borderRadius: 12,
          padding: "20px 24px",
          maxWidth: 440,
        },
      }}
    >
      {/* Header */}
      <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
        <Text variant="large" styles={{ root: { fontWeight: 700, color: PURVIEW_PURPLE } }}>
          {data.productAbbr}
        </Text>
        {isCapped && (
          <Stack
            horizontalAlign="center"
            verticalAlign="center"
            styles={{ root: { backgroundColor: "#FFF3E0", borderRadius: 12, padding: "2px 10px", border: "1px solid #E65100" } }}
          >
            <Text variant="xSmall" styles={{ root: { fontWeight: 700, color: "#E65100" } }}>CAPPED</Text>
          </Stack>
        )}
        {isNull && (
          <Stack
            horizontalAlign="center"
            verticalAlign="center"
            styles={{ root: { backgroundColor: "#EEEEEE", borderRadius: 12, padding: "2px 10px" } }}
          >
            <Text variant="xSmall" styles={{ root: { fontWeight: 700, color: "#757575" } }}>NULL</Text>
          </Stack>
        )}
      </Stack>

      {/* Factor boxes */}
      <Stack horizontal tokens={{ childrenGap: 8 }}>
        <FactorBox label="MPU" value={fmtInt(data.mpu)} />
        <FactorBox label="PAU" value={data.pau === null ? "NULL" : fmtInt(data.pau)} valueColor={data.pau === null ? "#D13438" : "#323130"} />
        <FactorBox label="Deploy Rate" value={displayRate} valueColor={rateColor} />
      </Stack>

      {/* Scoring bins */}
      {data.scoringBins && (
        <>
          <div style={{ height: 1, backgroundColor: "#E8D5F0" }} />
          <Stack tokens={{ childrenGap: 4 }}>
            <Text variant="small" styles={{ root: { fontWeight: 700, color: PURVIEW_PURPLE } }}>Scoring Bins:</Text>
            <Text variant="small" styles={{ root: { color: "#323130", lineHeight: "18px" } }}>{data.scoringBins}</Text>
          </Stack>
        </>
      )}
    </Stack>
  );
};

export default PurviewDeploymentRawIndicatorCards;
